// Component copy to CreateScheduledTournamentForm.tsx on create-scheduled-tournament
import { useEffect, useState } from 'react'
import { DS } from '../../design-system'
import { StyleSheet, TextInput, Switch } from 'react-native'
import { Dropdown } from './Dropdown'
import { translate } from '../../i18n'
import { DateTimePickers } from './DateTimePickers'
import { MAX_NAME_LENGTH } from './create-tournament-screen'
import { ACTION_TIME_VALUES, BLIND_TIME_VALUES, STARTING_CHIPS_VALUES } from '@/constants'

interface CreateTournamentFormProps {
  isNameEmpty?: boolean
  setIsNameEmpty?: (value: boolean) => void
  tournamentName: string
  tournamentBlindsLength: string
  tournamentStartingChips: string
  tournamentActionTime: string
  nameTooLong?: boolean
  setTournamentName: (value: string) => void
  setTournamentBlindsLength: (value: string) => void
  setTournamentStartingChips: (value: string) => void
  setTournamenActionTime: (value: string) => void
  setScheduleForLater: (value: boolean) => void
  scheduleForLater: boolean
  isEdit?: boolean
}
const STARTING_CHIPS = 10000

export const CreateTournamentForm = ({
  isNameEmpty = false,
  setIsNameEmpty,
  nameTooLong = false,
  tournamentName,
  tournamentBlindsLength,
  tournamentStartingChips,
  tournamentActionTime,
  setTournamentName,
  setTournamentBlindsLength,
  setTournamentStartingChips,
  setTournamenActionTime,
  setScheduleForLater,
  scheduleForLater,
  isEdit
}: CreateTournamentFormProps) => {
  const [defaultBlinds, setDefaultBlinds] = useState(BLIND_TIME_VALUES[0])
  const [defaultStartingChips, setDefaultStartingChips] = useState(STARTING_CHIPS.toString())
  const [defaultActionTime, setDefaultActionTime] = useState(ACTION_TIME_VALUES[1])

  const handleBlindsChange = (selectedItem: string) => {
    const selectedValue = selectedItem.replace(' min', '')
    setTournamentBlindsLength(selectedValue)
  }

  const handleActionTimeChange = (selectedItem: string) => {
    const selectedValue = selectedItem.replace(' sec', '')
    setTournamenActionTime(selectedValue)
  }

  useEffect(() => {
    tournamentBlindsLength ? setDefaultBlinds(`${tournamentBlindsLength} min`) : setDefaultBlinds(BLIND_TIME_VALUES[0])
    tournamentStartingChips
      ? setDefaultStartingChips(tournamentStartingChips)
      : setDefaultStartingChips(STARTING_CHIPS.toString())
    tournamentActionTime
      ? setDefaultActionTime(`${tournamentActionTime} sec`)
      : setDefaultActionTime(ACTION_TIME_VALUES[1])
  }, [tournamentBlindsLength, tournamentStartingChips, tournamentActionTime])

  return (
    <DS.Frame style={styles.container}>
      <DS.Text variant="Heading 4/Regular">{translate('createTournamentScreen.formTitle')}</DS.Text>
      <DS.Frame gap="8">
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('createTournamentScreen.name')}
        </DS.Text>
        <TextInput
          style={[styles.input, isNameEmpty ? styles.nameIsEmpty : {}]}
          value={tournamentName}
          onChangeText={(value) => {
            setTournamentName(value)
            setIsNameEmpty?.(false)
          }}
        />
        {nameTooLong && (
          <DS.Text variant="Caption/Regular" color="Utility/Warning">
            {translate('createTournamentScreen.nameTooLong', { length: MAX_NAME_LENGTH })}
          </DS.Text>
        )}
      </DS.Frame>
      <DS.Frame gap="8">
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('createTournamentScreen.blindsLength')}
        </DS.Text>
        <Dropdown
          data={BLIND_TIME_VALUES}
          defaultValue={defaultBlinds}
          onSelect={(selectedItem) => handleBlindsChange(selectedItem)}
        />
      </DS.Frame>
      <DS.Frame gap="8">
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('createTournamentScreen.startingChips')}
        </DS.Text>
        <Dropdown
          data={STARTING_CHIPS_VALUES}
          defaultValue={defaultStartingChips}
          onSelect={(selectedItem) => setTournamentStartingChips(selectedItem)}
        />
      </DS.Frame>
      <DS.Frame gap="8">
        <DS.Text variant="Body Medium/Medium" color="Global/Black">
          {translate('createTournamentScreen.actionTime')}
        </DS.Text>
        <Dropdown
          data={ACTION_TIME_VALUES}
          defaultValue={defaultActionTime}
          onSelect={(selectedItem) => handleActionTimeChange(selectedItem)}
        />
      </DS.Frame>
      {!isEdit && (
        <DS.Frame style={styles.switchContainer}>
          <DS.Text variant="Heading 4/Regular" color="Global/Black">
            {translate('createTournamentScreen.schedule')}
          </DS.Text>
          <Switch
            onValueChange={setScheduleForLater}
            value={scheduleForLater}
            trackColor={{ false: DS.palette['Brand/Light Grey'], true: DS.palette['Brand/Maroon'] }}
            thumbColor={DS.palette['Global/White']}
          />
        </DS.Frame>
      )}
      {scheduleForLater && !isEdit ? <DateTimePickers /> : null}
    </DS.Frame>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: DS.palette['Global/White'],
    padding: 24,
    gap: 24,
    borderRadius: 16
  },
  input: {
    height: 48,
    borderRadius: 5,
    paddingStart: 10,
    marginTop: 6,
    borderColor: DS.palette['Global/Black'],
    borderWidth: 1
  },
  nameIsEmpty: {
    borderColor: DS.palette['Utility/Warning']
  },
  dropdown: {
    height: 48,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 5,
    paddingStart: 10,
    marginTop: 6,
    borderColor: DS.palette['Global/Black'],
    borderWidth: 1,
    width: '100%'
  },
  dropdownPickerImage: {
    width: 16,
    height: 16,
    resizeMode: 'contain',
    tintColor: DS.palette['Global/Black']
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  }
})
